import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { CreditCard, Briefcase, CheckCircle, AlertCircle, Loader2 } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const PaywallModal = ({ open, onOpenChange, quota, token, onPaymentSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const loadRazorpayScript = () => {
    return new Promise((resolve) => {
      if (window.Razorpay) {
        resolve(true);
        return;
      }
      const script = document.createElement("script");
      script.src = "https://checkout.razorpay.com/v1/checkout.js";
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  const handleBuyCredits = async () => {
    setLoading(true);
    setError(null);

    try {
      // Load Razorpay script if not already loaded
      const scriptLoaded = await loadRazorpayScript();
      if (!scriptLoaded) {
        throw new Error("Failed to load payment gateway");
      }

      // Create order
      const orderRes = await fetch(`${API_URL}/api/payments/create-order`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ quantity: 1 }),
      });

      if (!orderRes.ok) {
        const errData = await orderRes.json();
        throw new Error(errData.detail || "Failed to create order");
      }

      const orderData = await orderRes.json();

      // Open Razorpay checkout
      const options = {
        key: orderData.key_id,
        amount: orderData.amount,
        currency: orderData.currency,
        name: "OneWay",
        description: "50 Job Credits",
        order_id: orderData.order_id,
        handler: async function (response) {
          // Verify payment
          try {
            const verifyRes = await fetch(`${API_URL}/api/payments/verify`, {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
              },
              body: JSON.stringify({
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
              }),
            });

            if (!verifyRes.ok) {
              const errData = await verifyRes.json();
              throw new Error(errData.detail || "Payment verification failed");
            }

            const verifyData = await verifyRes.json();
            
            // Success! Call the callback to update parent state
            if (onPaymentSuccess) {
              onPaymentSuccess(verifyData);
            }
            
            onOpenChange(false);
          } catch (err) {
            setError(err.message);
          } finally {
            setLoading(false);
          }
        },
        prefill: {
          name: "",
          email: "",
        },
        theme: {
          color: "#4A728D",
        },
        modal: {
          ondismiss: function () {
            setLoading(false);
          },
        },
      };

      const razorpay = new window.Razorpay(options);
      razorpay.on("payment.failed", function (response) {
        setError(`Payment failed: ${response.error.description}`);
        setLoading(false);
      });
      razorpay.open();
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="paywall-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <AlertCircle className="h-6 w-6 text-amber-500" />
            Job Limit Reached
          </DialogTitle>
          <DialogDescription className="text-base">
            You&apos;ve used all your available job tracking slots.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Current Usage */}
          <div className="bg-slate-50 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Briefcase className="h-5 w-5 text-[#4A728D]" />
                <span className="font-medium text-slate-700">Jobs Used</span>
              </div>
              <span className="text-lg font-bold text-[#4A728D]">
                {quota?.jobs_used || 0} / {quota?.jobs_limit || 15}
              </span>
            </div>
            <div className="mt-2 h-2 bg-slate-200 rounded-full overflow-hidden">
              <div
                className="h-full bg-amber-500 rounded-full transition-all"
                style={{
                  width: `${Math.min(100, ((quota?.jobs_used || 0) / (quota?.jobs_limit || 15)) * 100)}%`,
                }}
              />
            </div>
          </div>

          {/* Credit Pack Info */}
          <div className="border rounded-lg p-4 bg-gradient-to-br from-[#4A728D]/5 to-[#A3C3D8]/10">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-[#4A728D] rounded-lg">
                <CreditCard className="h-5 w-5 text-white" />
              </div>
              <div className="flex-1">
                <h4 className="font-semibold text-slate-800">Job Credit Pack</h4>
                <p className="text-sm text-slate-600 mt-1">
                  Unlock 50 more job tracking slots
                </p>
                <div className="mt-3 flex items-baseline gap-1">
                  <span className="text-2xl font-bold text-[#4A728D]">₹200</span>
                  <span className="text-sm text-slate-500">/ 50 jobs</span>
                </div>
              </div>
            </div>

            <div className="mt-4 space-y-2">
              <div className="flex items-center gap-2 text-sm text-slate-600">
                <CheckCircle className="h-4 w-4 text-green-500" />
                <span>Track 50 additional job applications</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-slate-600">
                <CheckCircle className="h-4 w-4 text-green-500" />
                <span>Full access to all features</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-slate-600">
                <CheckCircle className="h-4 w-4 text-green-500" />
                <span>Credits never expire</span>
              </div>
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="text-sm text-red-600 bg-red-50 p-3 rounded-lg">
              {error}
            </div>
          )}

          {/* Buy Button */}
          <Button
            className="w-full bg-[#4A728D] hover:bg-[#3a5a70] text-white py-6 text-lg"
            onClick={handleBuyCredits}
            disabled={loading}
            data-testid="buy-credits-btn"
          >
            {loading ? (
              <>
                <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                Processing...
              </>
            ) : (
              <>
                <CreditCard className="mr-2 h-5 w-5" />
                Buy 50 Jobs for ₹200
              </>
            )}
          </Button>

          <p className="text-xs text-center text-slate-500">
            Secure payment powered by Razorpay
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PaywallModal;
